import { isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  Directive,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  PLATFORM_ID,
} from '@angular/core';

export interface DragPosition {
  x: number;
  y: number;
}

@Directive({
  selector: '[appDraggable]',
  standalone: true,
})
export class DraggableDirective implements OnDestroy {
  readonly disabled = input<boolean>(false);
  readonly dragged = output<DragPosition>();

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly platform = inject(PLATFORM_ID);

  private isDragging = false;
  private startX = 0;
  private startY = 0;
  private origX = 0;
  private origY = 0;

  private rafId: number | null = null;
  private pendingEvent: MouseEvent | null = null;

  private readonly onMouseDown = (e: MouseEvent) => this.startDrag(e);
  private readonly onMouseMove = (e: MouseEvent) => this.queueDrag(e);
  private readonly onMouseUp = () => this.stopDrag();

  constructor() {
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platform)) return;
      this.el.nativeElement.addEventListener('mousedown', this.onMouseDown);
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('mouseup', this.onMouseUp);
    });
  }

  ngOnDestroy(): void {
    this.el.nativeElement.removeEventListener('mousedown', this.onMouseDown);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private startDrag(e: MouseEvent): void {
    if (this.disabled()) return;
    // Chỉ drag khi click trực tiếp vào titlebar, không phải button con
    if ((e.target as HTMLElement).closest('button')) return;

    this.isDragging = true;
    this.startX = e.clientX;
    this.startY = e.clientY;

    // Modal là position:fixed nên lấy getBoundingClientRect thay vì offsetLeft
    const rect =
      this.el.nativeElement.closest('.call-modal')?.getBoundingClientRect() ??
      this.el.nativeElement.getBoundingClientRect();
    this.origX = rect.left;
    this.origY = rect.top;

    this.el.nativeElement.style.cursor = 'grabbing';
    e.preventDefault();
  }

  /** Gom nhiều sự kiện mousemove trong cùng 1 frame lại thành 1 lần tính toán, tránh reflow liên tục */
  private queueDrag(e: MouseEvent): void {
    if (!this.isDragging) return;

    this.pendingEvent = e;
    if (this.rafId !== null) return;

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      if (this.pendingEvent) {
        this.onDrag(this.pendingEvent);
        this.pendingEvent = null;
      }
    });
  }

  private onDrag(e: MouseEvent): void {
    if (!this.isDragging) return;

    const modal =
      (this.el.nativeElement.closest('.call-modal') as HTMLElement) ?? this.el.nativeElement;
    const modalW = modal.offsetWidth;
    const modalH = modal.offsetHeight;

    // Clamp trong window viewport
    const nx = Math.max(
      0,
      Math.min(window.innerWidth - modalW, this.origX + e.clientX - this.startX),
    );
    const ny = Math.max(
      0,
      Math.min(window.innerHeight - modalH, this.origY + e.clientY - this.startY),
    );

    this.dragged.emit({ x: nx, y: ny });
  }

  private stopDrag(): void {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.el.nativeElement.style.cursor = 'grab';

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.pendingEvent = null;
  }
}
